/* Phase 14A-b3 push-fix -- Home one-liner nudge.
 *
 * Sharda Assistant polite reminder shown only when browser Notification
 * permission is NOT `granted`. Dismissible for the session (sessionStorage,
 * not localStorage -- reappears in a fresh tab / tomorrow).
 *
 * Two states:
 *   * permission === "default" -> "[Enable now]" CTA fires
 *     `Notification.requestPermission()` then subscribes.
 *   * permission === "denied"  -> shows how to re-enable from browser settings.
 */
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, X, Loader2, Info, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  pushSupported, currentPermission, hasActivePushSubscription,
  subscribeToPush,
} from "@/lib/pushClient";

const SESSION_KEY = "sharda_push_nudge_dismissed";

export default function PushNudge() {
  const [visible, setVisible] = useState(false);
  const [permission, setPermission] = useState("default");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      if (!pushSupported()) return;
      const perm = await currentPermission();
      setPermission(perm);
      if (perm === "granted") {
        const has = await hasActivePushSubscription();
        if (has) return;  // fully set up, nothing to nudge
      }
      if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(SESSION_KEY) === "1") return;
      setVisible(true);
    })();
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      await subscribeToPush();
      const perm = await currentPermission();
      setPermission(perm);
      if (perm === "granted") {
        toast.success("Push notifications enabled");
        setVisible(false);
      } else if (perm === "denied") {
        toast.error("You blocked notifications. Re-enable them from your browser's site settings.");
      }
    } catch (e) {
      toast.error(e?.message || "Could not enable push");
    } finally { setBusy(false); }
  };

  const dismiss = () => {
    if (typeof sessionStorage !== "undefined") sessionStorage.setItem(SESSION_KEY, "1");
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
          className="mb-4 rounded-xl border border-sky-500/25 bg-gradient-to-r from-brand-blue/25 to-sky-500/10 backdrop-blur-md px-4 py-2.5 flex items-center gap-3"
          data-testid="home-push-nudge"
        >
          <div className="h-8 w-8 rounded-lg bg-sky-500/20 border border-sky-500/40 flex items-center justify-center shrink-0">
            <Bell className="h-4 w-4 text-sky-300" />
          </div>
          <div className="flex-1 min-w-0 text-[13px]">
            <span className="font-semibold text-white">Sharda Assistant · </span>
            <span className="text-foreground/70">
              Push notifications:{" "}
              {permission === "granted" ? (
                <span className="text-emerald-300 inline-flex items-center gap-1">
                  <Check className="h-3 w-3" /> Enabled — subscribing this device...
                </span>
              ) : permission === "denied" ? (
                <span className="text-amber-300 inline-flex items-center gap-1">
                  <Info className="h-3 w-3" /> Blocked. Re-enable from browser site settings.
                </span>
              ) : (
                <span className="text-sky-300">Disabled — get notified when messages arrive.</span>
              )}
            </span>
          </div>
          {permission !== "denied" && (
            <Button
              size="sm" onClick={enable} disabled={busy}
              className="bg-gradient-primary text-white shrink-0 h-7 px-3"
              data-testid="home-push-nudge-enable"
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin mr-1.5" /> : <Bell className="h-3 w-3 mr-1.5" />}
              Enable now
            </Button>
          )}
          <Button
            size="sm" variant="ghost" onClick={dismiss}
            className="shrink-0 text-foreground/60 h-7 w-7 p-0"
            data-testid="home-push-nudge-dismiss"
            aria-label="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
